import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, setDoc, updateDoc, arrayUnion, getDoc } from "firebase/firestore";
import Lottie from "lottie-react";
import loadingAnimation from "../lottie/loading-animation.json";

// Helper to generate a unique ID in a Firestore collection
async function getUniqueId(db, collectionName, prefix, minLength = 5) {
  let attempts = 0;
  let length = minLength;
  while (true) {
    const candidate = prefix + Math.random().toString(36).substr(2, length);
    const docRef = doc(db, collectionName, candidate);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return candidate;
    }
    attempts++;
    if (attempts % 10 === 0) length++;
  }
}

const generateShareCode = () => "u" + Math.random().toString(36).substr(2, 5);

function AddQuestionsSection({ auth, db, checkAccountType }) {
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState("");
  const [correctAnswer, setCorrectAnswer] = useState("");
  const [explanation, setExplanation] = useState("");
  const [message, setMessage] = useState("");
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonInput, setJsonInput] = useState("");

  useEffect(() => {
    const fetchFolders = async () => {
      if (!auth.currentUser) return;
      try {
        const userUID = auth.currentUser.uid;
        const quizzesRef = collection(db, "userQuizzes");
        const snapshot = await getDocs(quizzesRef);
        const fetchedFolders = snapshot.docs
          .filter(docSnap => docSnap.data().ownerId === userUID)
          .map(docSnap => ({
            id: docSnap.id,
            name: docSnap.data().title || docSnap.id,
          }));
        setFolders(fetchedFolders);
      } catch (error) {
        console.error("Failed to fetch folders:", error.message);
      }
    };
    fetchFolders();
  }, [auth, db]);

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    if (!checkAccountType()) return;
    if (!auth.currentUser) {
      setMessage("User is not authenticated!");
      return;
    }
    if (!selectedFolder && !newFolderName) {
      setMessage("Please select an existing folder or create a new one.");
      return;
    }
    if (!question || !options || !correctAnswer) {
      setMessage("Please fill out all fields.");
      return;
    }
    try {
      let quizId = selectedFolder;
      let shareCode;
      let justCreated = false;
      if (!quizId) {
        quizId = await getUniqueId(db, "userQuizzes", "quiz-");
        shareCode = await getUniqueId(db, "shareCodeLinks", "u");
        await setDoc(doc(db, "userQuizzes", quizId), {
          title: newFolderName,
          ownerId: auth.currentUser.uid,
          createdAt: new Date(),
          shareCode,
        });
        // Create shareCodeLinks entry
        await setDoc(doc(db, "shareCodeLinks", shareCode), {
          linksTo: quizId,
          ownerId: auth.currentUser.uid,
        });
        // Add shareCode to user's downloadedTopics
        const userRef = doc(db, "users", auth.currentUser.uid);
        await updateDoc(userRef, {
          downloadedTopics: arrayUnion(shareCode),
        });
        // Add to folders state so it appears in dropdown
        setFolders(prev => [
          ...prev,
          { id: quizId, name: newFolderName }
        ]);
        setSelectedFolder(quizId); // Select the newly created folder
        justCreated = true;
      }
      const questionId = `question-${Date.now()}`;
      await setDoc(doc(db, "userQuizzes", quizId, "questions", questionId), {
        question,
        options: options.split(",").map(opt => opt.trim()),
        correctAnswer,
        explanation,
      });
      setMessage("Question added successfully!");
      if (justCreated) setNewFolderName("");
      setQuestion("");
      setOptions("");
      setCorrectAnswer("");
      setExplanation("");
    } catch (error) {
      setMessage("Failed to add question. Please try again.");
    }
  };

  const handleJsonInputChange = (e) => setJsonInput(e.target.value);
  const handleJsonSubmit = async () => {
    if (!checkAccountType()) return;
    if (!auth.currentUser) {
      setMessage("User is not authenticated!");
      return;
    }
    if (!selectedFolder && !newFolderName) {
      setMessage("Please select an existing folder or create a new one.");
      return;
    }
    try {
      const jsonData = JSON.parse(jsonInput);
      if (!Array.isArray(jsonData)) {
        setMessage("Invalid JSON format. Please provide an array of questions.");
        return;
      }
      let quizId = selectedFolder;
      let shareCode;
      let justCreated = false;
      if (!quizId) {
        quizId = await getUniqueId(db, "userQuizzes", "quiz-");
        shareCode = await getUniqueId(db, "shareCodeLinks", "u");
        await setDoc(doc(db, "userQuizzes", quizId), {
          title: newFolderName,
          ownerId: auth.currentUser.uid,
          createdAt: new Date(),
          shareCode,
        });
        // Create shareCodeLinks entry
        await setDoc(doc(db, "shareCodeLinks", shareCode), {
          linksTo: quizId,
          ownerId: auth.currentUser.uid,
        });
        // Add shareCode to user's downloadedTopics
        const userRef = doc(db, "users", auth.currentUser.uid);
        await updateDoc(userRef, {
          downloadedTopics: arrayUnion(shareCode),
        });
        // Add to folders state so it appears in dropdown
        setFolders(prev => [
          ...prev,
          { id: quizId, name: newFolderName }
        ]);
        setSelectedFolder(quizId); // Select the newly created folder
        justCreated = true;
      }
      for (const q of jsonData) {
        const questionId = `question-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        await setDoc(doc(db, "userQuizzes", quizId, "questions", questionId), q);
      }
      setMessage("Questions added successfully!");
      if (justCreated) setNewFolderName("");
      setJsonInput("");
    } catch (error) {
      setMessage("Failed to parse JSON. Please check the format.");
    }
  };

  if (!navigator.onLine) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "calc(100vh - 130px)",
          flexDirection: "column",
          background: "#0F0F0F",
          transform: "translateY(-130px)",
        }}
      >
        <div
          style={{
            overflow: "hidden",
            width: "200px",
            height: "150px", // Cropped height
          }}
        >
          <Lottie
            animationData={loadingAnimation}
            loop
            style={{ width: "200px", height: "200px" }}
          />
        </div>
        <p style={{ color: "#f8f8f5", marginTop: "20px" }}>
          Connect to the internet for this function
        </p>
      </div>
    );
  }

  return (
    <div
      className="w-full max-w-4xl mx-auto text-[#f8f8f5]"
      style={{
        padding: "0 0 140px 0", // Remove left and right padding, keep bottom padding
        boxSizing: "border-box",
        minHeight: "100vh",
        overflowY: "auto", // Enable scrolling
        background: "#0F0F0F", // Set actual background color
      }}
    >
      {/* Vertical slider for selecting topics */}
      <div
        className="overflow-x-auto pb-2"
        
      >
        {/* Slider content here */}
      </div>

      <div
        className="bg-[#191919] p-6 rounded-[40px]"
        style={{
          margin: "0px auto", // Center the card
          width: "98%", // Adjusted width for mobile
          maxWidth: 400, // Ensure it doesn't exceed max width
          padding: 32, // Adjusted padding for better fit
        }}
      >
        {!jsonMode ? (
          <form onSubmit={handleAddQuestion}>
            <div style={{ marginTop: "25px" }}>
              <label className="block mb-2 text-[#f8f8f5]" style={{ marginTop: "10px" }}>Select an Existing Folder</label>
              <select
                className="w-full p-3 rounded-md bg-[#232323] text-[#f8f8f5] border border-transparent focus:border-[#f8f8f5] focus:ring-2 focus:ring-[#f8f8f5] transition-colors duration-300"
                value={selectedFolder}
                onChange={e => setSelectedFolder(e.target.value)}
              >
                <option value="">-- Select Folder --</option>
                {folders.map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {folder.name}
                  </option>
                ))}
              </select>
            </div>
            {!selectedFolder && (
              <div>
                <label className="block mb-2 text-[#f8f8f5]" style={{ marginTop: "10px" }}>New Folder Name</label>
                <input
                  type="text"
                  className="w-full p-3 rounded-md bg-[#232323] text-[#f8f8f5] border border-transparent focus:border-[#f8f8f5] focus:ring-2 focus:ring-[#f8f8f5] transition-colors duration-300"
                  value={newFolderName}
                  onChange={e => setNewFolderName(e.target.value)}
                />
              </div>
            )}
            <div>
              <label className="block mb-2 text-[#f8f8f5]" style={{ marginTop: "10px" }}>Question</label>
              <input
                type="text"
                className="w-full p-3 rounded-md bg-[#232323] text-[#f8f8f5] border border-transparent focus:border-[#f8f8f5] focus:ring-2 focus:ring-[#f8f8f5] transition-colors duration-300"
                value={question}
                onChange={e => setQuestion(e.target.value)}
              />
            </div>
            <div>
              <label className="block mb-2 text-[#f8f8f5]" style={{ marginTop: "10px" }}>Options (comma separated)</label>
              <input
                type="text"
                className="w-full p-3 rounded-md bg-[#232323] text-[#f8f8f5] border border-transparent focus:border-[#f8f8f5] focus:ring-2 focus:ring-[#f8f8f5] transition-colors duration-300"
                value={options}
                onChange={e => setOptions(e.target.value)}
              />
            </div>
            <div>
              <label className="block mb-2 text-[#f8f8f5]" style={{ marginTop: "10px" }}>Correct Answer</label>
              <input
                type="text"
                className="w-full p-3 rounded-md bg-[#232323] text-[#f8f8f5] border border-transparent focus:border-[#f8f8f5] focus:ring-2 focus:ring-[#f8f8f5] transition-colors duration-300"
                value={correctAnswer}
                onChange={e => setCorrectAnswer(e.target.value)}
              />
            </div>
            <div>
              <label className="block mb-2 text-[#f8f8f5]" style={{ marginTop: "10px" }}>Explanation (optional)</label>
              <textarea
                className="w-full p-3 rounded-md bg-[#232323] text-[#f8f8f5] border border-transparent focus:border-[#f8f8f5] focus:ring-2 focus:ring-[#f8f8f5] transition-colors duration-300"
                value={explanation}
                onChange={e => setExplanation(e.target.value)}
              />
            </div>
            <button
              type="submit"
              className="w-full py-3 px-6 bg-[#f8f8f5] hover:bg-[#e5e5e0] text-[#161616] rounded-2xl transition-colors duration-300 font-semibold"
              style={{ marginTop: "10px" }}
            >
              Add Question
            </button>
          </form>
        ) : (
          <div>
            <div style={{ marginTop: "25px" }}>
              <label className="block mb-2 text-[#f8f8f5]" style={{ marginTop: "10px" }}>Select an Existing Folder</label>
              <select
                className="w-full p-3 rounded-md bg-[#232323] text-[#f8f8f5] border border-transparent focus:border-[#f8f8f5] focus:ring-2 focus:ring-[#f8f8f5] transition-colors duration-300"
                value={selectedFolder}
                onChange={e => setSelectedFolder(e.target.value)}
              >
                <option value="">-- Select Folder --</option>
                {folders.map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {folder.name}
                  </option>
                ))}
              </select>
            </div>
            {!selectedFolder && (
              <div>
                <label className="block mb-2 text-[#f8f8f5]" style={{ marginTop: "10px" }}>New Folder Name</label>
                <input
                  type="text"
                  className="w-full p-3 rounded-md bg-[#232323] text-[#f8f8f5] border border-transparent focus:border-[#f8f8f5] focus:ring-2 focus:ring-[#f8f8f5] transition-colors duration-300"
                  value={newFolderName}
                  onChange={e => setNewFolderName(e.target.value)}
                />
              </div>
            )}
            <div className="mt-6">
              <label className="block mb-2 text-[#f8f8f5]">Paste JSON</label>
              <textarea
                className="w-full p-3 rounded-md bg-[#232323] text-[#f8f8f5] border border-transparent focus:border-[#f8f8f5] focus:ring-2 focus:ring-[#f8f8f5] transition-colors duration-300"
                value={jsonInput}
                onChange={handleJsonInputChange}
                rows={6}
              />
              <button
                type="button"
                onClick={handleJsonSubmit}
                className="w-full mt-3 py-3 px-6 bg-[#f8f8f5] hover:bg-[#e5e5e0] text-[#161616] rounded-2xl transition-colors duration-300 font-semibold"
              >
                Import JSON
              </button>
            </div>
          </div>
        )}
        <button
          type="button"
          className="w-full py-2 px-4 mt-2 bg-[#232323] hover:bg-[#222] text-[#f8f8f5] rounded-2xl transition-colors duration-300"
          onClick={() => setJsonMode((v) => !v)}
        >
          {jsonMode ? "Close JSON Mode" : "Switch to JSON Mode"}
        </button>
        {message && (
          <div className="mt-4 text-center text-green-400">{message}</div>
        )}
      </div>
    </div>
  );
}

export default AddQuestionsSection;
